/**
 * Tab management configuration and utilities
 * Handles opening new tabs with limit checking and tracking
 * 
 * Features:
 * - Centralized configuration constants
 * - Input validation and null checks
 * - Graceful error handling
 * - Tab title flashing as fallback when focus is blocked by browser
 * - CORS-safe implementation
 * - Clean error handling without verbose console logs
 */

// Configuration constants
const TAB_FLASH_MESSAGE = "🔔 SWITCH HERE!"
const TAB_FLASH_DURATION = 5000 // 5 seconds
const FOCUS_VERIFY_DELAY = 100 // milliseconds
const MAX_TREATMENT_TABS = 3

interface OpenTabOptions {
  route: string
  tabRefs: React.MutableRefObject<Record<string, Window>>
  setOpenTabExceededModal: (value: boolean) => void
  orgId?: string
  isAllowToViewAllOrg?: boolean
  allowMultipleSameRoute?: boolean
  ignoreQueryParams?: boolean
  matchExactRouteOnly?: boolean
}

/**
 * Gets the maximum allowed tab count from environment variables
 * Defaults to 3 if not set or invalid
 */
const getMaxTabCount = (): number => {
  const envValue = import.meta.env.VITE_ALLOWED_TAB_COUNT
  const parsed = parseInt(envValue, 10)
  return !isNaN(parsed) && parsed > 0 ? parsed : 3
}

/**
 * Strips query parameters from a URL
 * @param url - The full URL
 * @returns The URL without query parameters
 */
export const stripQueryParams = (url: string): string => {
  if (!url || typeof url !== "string") {
    return ""
  }
  return url.split("?")[0]
}

/**
 * Flashes the tab title to help user identify which tab is already open
 * @param windowRef - The window reference to flash
 * @param message - The message to show
 * @param duration - How long to flash in milliseconds
 */
const flashTabTitle = (
  windowRef: Window,
  message: string = TAB_FLASH_MESSAGE,
  duration: number = TAB_FLASH_DURATION
): void => {
  try {
    return;
    //can be enabled if needed
    const originalTitle = windowRef.document.title
    let count = 0
    const maxFlashes = Math.floor(duration / 500)
    
    const interval = setInterval(() => {
      try {
        if (windowRef.closed) {
          clearInterval(interval)
          return
        }
        
        windowRef.document.title = count % 2 === 0 ? message : originalTitle
        count++
        
        if (count >= maxFlashes) {
          clearInterval(interval)
          windowRef.document.title = originalTitle
        }
      } catch (e) {
        // Can't access document anymore (CORS or navigation)
        clearInterval(interval)
      }
    }, 500)
  } catch (error) {
    // Silently fail - tab might be on different origin
  }
}

/**
 * Attempts to focus a window using multiple methods
 * Flashes the tab title if focus fails due to browser restrictions
 * @param windowRef - The window reference to focus
 * @returns true if focus was attempted, false if window is invalid
 */
const focusWindow = (windowRef: Window): boolean => {
  if (!windowRef || windowRef.closed) {
    return false
  }

  let focusAttempted = false

  // Try direct focus (most common method)
  try {
    windowRef.focus()
    focusAttempted = true
  } catch (error) {
    // Focus failed, try alternative method
    try {
      window.blur()
      windowRef.focus()
      focusAttempted = true
    } catch (e) {
      // All focus attempts failed
    }
  }

  // Verify if focus worked and flash title if it didn't
  if (focusAttempted) {
    setTimeout(() => {
      try {
        if (!windowRef.document.hasFocus()) {
          flashTabTitle(windowRef)
        }
      } catch (e) {
        // Can't verify focus (CORS) - flash title to be safe
        flashTabTitle(windowRef)
      }
    }, FOCUS_VERIFY_DELAY)
  } else {
    // Focus wasn't attempted - flash title immediately
    flashTabTitle(windowRef)
  }

  return focusAttempted
}

/**
 * Extracts route parameters (patientId and caseId) from a route
 * @param route - The route to extract parameters from
 * @returns Object with patientId and caseId, or null if not found
 */
const extractRouteParams = (route: string): { patientId: string; caseId: string } | null => {
  if (!route || typeof route !== "string") {
    return null
  }
  
  // Remove query parameters first
  const baseRoute = stripQueryParams(route)
  
  // Match patterns like /patient/{patientId}/case/{caseId}
  const match = baseRoute.match(/\/patient\/(\d+)\/case\/(\d+)/)
  
  if (match && match[1] && match[2]) {
    return {
      patientId: match[1],
      caseId: match[2],
    }
  }
  
  return null
}

/**
 * Finds an existing tab with the same route parameters (patientId, caseId) AND same pathname
 * @param route - The route to search for
 * @param tabRefs - Reference to the tab tracking object
 * @returns The Window object if found, null otherwise
 */
const findTabBySameRouteParams = (
  route: string,
  tabRefs: React.MutableRefObject<Record<string, Window>>
): Window | null => {
  if (!route || !tabRefs?.current) {
    return null
  }
  
  const targetParams = extractRouteParams(route)
  const targetBaseRoute = stripQueryParams(route)
  
  if (!targetParams) {
    return null
  }
  
  for (const [key, windowRef] of Object.entries(tabRefs.current)) {
    if (!windowRef || windowRef.closed) {
      continue
    }
    
    const tabParams = extractRouteParams(key)
    const tabBaseRoute = stripQueryParams(key)
    
    // Check if patientId, caseId, AND base pathname all match
    if (
      tabParams &&
      tabParams.patientId === targetParams.patientId &&
      tabParams.caseId === targetParams.caseId &&
      tabBaseRoute === targetBaseRoute
    ) {
      return windowRef
    }
  }
  
  return null
}

/**
 * Finds an existing tab with the same caseId (regardless of pathname)
 * @param route - The route to search for (to extract caseId)
 * @param tabRefs - Reference to the tab tracking object
 * @returns Object with window reference and the tab key if found, null otherwise
 */
const findTabByCaseId = (
  route: string,
  tabRefs: React.MutableRefObject<Record<string, Window>>
): { windowRef: Window; tabKey: string } | null => {
  if (!route || !tabRefs?.current) {
    return null
  }
  
  const targetParams = extractRouteParams(route)
  if (!targetParams) {
    return null
  }
  
  for (const [key, windowRef] of Object.entries(tabRefs.current)) {
    if (!windowRef || windowRef.closed) {
      continue
    }
    
    try {
      // Try to read the current pathname from the window
      const currentPathname = windowRef.location.pathname
      const currentParams = extractRouteParams(currentPathname)
      
      if (currentParams && currentParams.caseId === targetParams.caseId) {
        return { windowRef, tabKey: key }
      }
    } catch (error) {
      // CORS error - fall back to comparing the stored key
      const keyParams = extractRouteParams(key)
      if (keyParams && keyParams.caseId === targetParams.caseId) {
        return { windowRef, tabKey: key }
      }
    }
  }
  
  return null
}

/**
 * Finds an existing tab with the same base URL (ignoring query parameters)
 * @param route - The route to search for
 * @param tabRefs - Reference to the tab tracking object
 * @returns The Window object if found, null otherwise
 */
export const findTabByBaseUrl = (
  route: string,
  tabRefs: React.MutableRefObject<Record<string, Window>>
): Window | null => {
  if (!route || !tabRefs?.current) {
    return null
  }
  
  const baseRoute = stripQueryParams(route)
  if (!baseRoute) {
    return null
  }
  
  for (const [key, windowRef] of Object.entries(tabRefs.current)) {
    if (!windowRef || windowRef.closed) {
      continue
    }
    
    try {
      // Try to read the current pathname from the window
      const currentPathname = windowRef.location.pathname
      const currentBaseRoute = stripQueryParams(currentPathname)
      
      if (currentBaseRoute === baseRoute) {
        return windowRef
      }
    } catch (error) {
      // CORS error - fall back to comparing the stored key
      const keyBaseRoute = stripQueryParams(key)
      if (keyBaseRoute === baseRoute) {
        return windowRef
      }
      
      // For timestamped keys, try accessing location.href
      try {
        const href = windowRef.location.href
        if (href && href.includes(baseRoute)) {
          return windowRef
        }
      } catch (e) {
        // Skip this tab
      }
    }
  }
  
  return null
}

/**
 * Opens a new tab with the specified route, respecting the tab limit
 * @param options - Configuration options for opening a new tab
 */
export const openNewTab = ({
  route,
  tabRefs,
  setOpenTabExceededModal,
  orgId,
  isAllowToViewAllOrg,
  allowMultipleSameRoute = false,
  ignoreQueryParams = false,
  matchExactRouteOnly = false,
}: OpenTabOptions) => {
  if (matchExactRouteOnly) {
    const matchingTab = findTabByBaseUrl(route, tabRefs)
    if (matchingTab) {
      focusWindow(matchingTab)
      return
    }
  } else {
    // First, check if a tab with the same caseId exists (regardless of pathname)
    const tabWithSameCaseId = findTabByCaseId(route, tabRefs)
    if (tabWithSameCaseId) {
      const { windowRef } = tabWithSameCaseId
      
      // Just focus the existing tab without changing its URL
      focusWindow(windowRef)
      return
    }
  }

  // If ignoring query params, check if any tab with the same base URL exists
  if (ignoreQueryParams) {
    const existingTab = findTabByBaseUrl(route, tabRefs)
    if (existingTab) {
      focusWindow(existingTab)
      return
    }
  }

  // If not allowing multiple same routes, check if tab with same route params exists
  if (!allowMultipleSameRoute) {
    // First check for exact route match
    if (tabRefs.current[route] && !tabRefs.current[route].closed) {
      focusWindow(tabRefs.current[route])
      return
    }
    
    // Then check for same route parameters (patientId and caseId)
    const tabWithSameParams = findTabBySameRouteParams(route, tabRefs)
    if (tabWithSameParams) {
      focusWindow(tabWithSameParams)
      return
    }
  }

  // Clean up closed tabs
  cleanupClosedTabs(tabRefs)

  // Check if the maximum number of tabs is already open
  const maxTabs = getMaxTabCount()
  if (Object.keys(tabRefs.current).length >= maxTabs) {
    setOpenTabExceededModal(true)
    return
  }

  // Build the full route with org prefix if needed
  const fullRoute = isAllowToViewAllOrg ? `/org/${orgId}${route}` : route

  // Open a new tab
  const newTab = window.open(fullRoute, "_blank")

  if (newTab) {
    // Generate a unique key for this tab if allowing multiple same routes
    const tabKey = allowMultipleSameRoute ? `${route}_${Date.now()}` : route
    tabRefs.current[tabKey] = newTab

    // Add event listener to detect when the tab is manually closed
    newTab.onload = function () {
      newTab.addEventListener("unload", function () {
        delete tabRefs.current[tabKey]
      })
    }
  }
}

/**
 * Checks if a tab is still in new patient flow (not on treatment page or order page or closed)
 * @param windowRef - The window reference to check
 * @returns false if the tab is on the treatment/order page or closed, true otherwise
 */
const isTabStillInNewPatientFlow = (windowRef: Window | null | undefined): boolean => {
  if (!windowRef || windowRef.closed) {
    return false
  }
  
  try {
    const pathname = windowRef.location.pathname
    if (!pathname) {
      return true
    }
    
    const params = new URLSearchParams(window.location.search);

    // Check if the case is past a initial new patient setup routes
    return !(
      (pathname.includes('/clinical/') && pathname.includes('/treat')) 
      || pathname.includes('/order/patient/')
      || pathname.includes('/summary/confirmation')
      || (pathname.includes('/records/patient/') && pathname.includes('/rxForm'))
      || (pathname.includes('/records/patient/') && params.get("from")=="clinical" && params.get("action")=="submit_to_uassist" )
    )
  } catch (error) {
    // If we can't access the location (CORS), assume it's ok to open new tab
    return true
  }
}

/**
 * Counts how many tabs are currently on the treatment page
 * @param tabRefs - Reference to the tab tracking object
 * @returns Number of tabs currently showing treatment pages
 */
const countTreatmentTabs = (
  tabRefs: React.MutableRefObject<Record<string, Window>>
): number => {
  if (!tabRefs?.current) {
    return 0
  }
  
  let count = 0
  
  Object.entries(tabRefs.current).forEach(([key, windowRef]) => {
    if (!windowRef || windowRef.closed) {
      return
    }
    
    try {
      const pathname = windowRef.location.pathname
      if (pathname && pathname.includes('/clinical/') && pathname.includes('/treat')) {
        count++
      }
    } catch (error) {
      // If we can't read the path, check the key as fallback
      if (key && key.includes('/clinical/') && key.includes('/treat')) {
        count++
      }
    }
  })
  
  return count
}

/**
 * Finds the most recently opened patient creation tab
 * @param tabRefs - Reference to the tab tracking object
 * @returns The Window object of the most recent patient tab, or null
 */
const findRecentPatientTab = (
  tabRefs: React.MutableRefObject<Record<string, Window>>
): Window | null => {
  if (!tabRefs?.current) {
    return null
  }
  
  const patientTabKeys = Object.keys(tabRefs.current).filter(key => 
    key && key.startsWith('/patients/new')
  )
  
  if (patientTabKeys.length === 0) {
    return null
  }
  
  // Sort by timestamp (descending) to get the most recent
  patientTabKeys.sort((a, b) => {
    const timeA = parseInt(a.split('_')[1] || '0', 10)
    const timeB = parseInt(b.split('_')[1] || '0', 10)
    return timeB - timeA
  })
  
  const mostRecentKey = patientTabKeys[0]
  if (!mostRecentKey) {
    return null
  }
  
  const windowRef = tabRefs.current[mostRecentKey]
  
  // Check if the window is still valid
  if (windowRef && !windowRef.closed) {
    return windowRef
  }
  
  return null
}

/**
 * Opens a new patient form tab
 * Smart behavior: 
 * - If there are already MAX_TREATMENT_TABS treatment tabs open, shows tab limit exceeded modal
 * - If a previously opened patient tab exists and is NOT on the treatment page, focuses it
 * - If the previous tab is on the treatment page or closed, allows opening a new tab
 */
export const openNewPatientTab = ({
  tabRefs,
  setOpenTabExceededModal,
  orgId,
  isAllowToViewAllOrg,
}: Omit<OpenTabOptions, "route" | "allowMultipleSameRoute" | "ignoreQueryParams">) => {
  // Check if there are already MAX_TREATMENT_TABS treatment tabs open
  const treatmentTabCount = countTreatmentTabs(tabRefs)
  if (treatmentTabCount >= MAX_TREATMENT_TABS) {
    setOpenTabExceededModal(true)
    return
  }
  
  // Find the most recently opened patient tab
  const recentPatientTab = findRecentPatientTab(tabRefs)
  
  // If there's a recent patient tab and it's not past the rxForm pages, then focus it
  if (recentPatientTab && isTabStillInNewPatientFlow(recentPatientTab)) {
    focusWindow(recentPatientTab)
    return
  }
  
  // Otherwise, open a new tab
  openNewTab({
    route: "/patients/new?from=patientList",
    tabRefs,
    setOpenTabExceededModal,
    orgId,
    isAllowToViewAllOrg,
    allowMultipleSameRoute: true, // Allow multiple tabs with same route
    ignoreQueryParams: false,
  })
}

/**
 * Opens a case/clinical tab
 * If a tab with the same base URL (ignoring query params) is already open, focuses it
 * @param route - The route to open
 * @param tabRefs - Reference to the tab tracking object
 * @param setOpenTabExceededModal - Function to show tab limit exceeded modal
 * @param orgId - Organization ID
 * @param isAllowToViewAllOrg - Whether user can view all organizations
 */
export const openCaseTab = ({
  route,
  tabRefs,
  setOpenTabExceededModal,
  orgId,
  isAllowToViewAllOrg,
}: Omit<OpenTabOptions, "allowMultipleSameRoute" | "ignoreQueryParams">) => {
  openNewTab({
    route,
    tabRefs,
    setOpenTabExceededModal,
    orgId,
    isAllowToViewAllOrg,
    allowMultipleSameRoute: false,
    ignoreQueryParams: true, // Focus existing tab if same base URL exists
  })
}

/**
 * Cleans up closed tabs from the tracking object
 * @param tabRefs - Reference to the tab tracking object
 */
export const cleanupClosedTabs = (
  tabRefs: React.MutableRefObject<Record<string, Window>>
) => {
  Object.keys(tabRefs.current).forEach((key) => {
    if (tabRefs.current[key]?.closed) {
      delete tabRefs.current[key]
    }
  })
}

